import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import {
  selectIssuesData,
  selectIssuesLoading,
  selectIssuesError,
} from "../../Redux/Slices/issuesSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import Typography from "../../Utility/Typography";
import IssueList from "./IssueList";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForDropdown,
  mapSystemListItemToOption,
  ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
} from "./issueMappers";

/**
 * All Issues list — filter state is owned by Issues.jsx and passed down as props.
 * Reference data (users, projects, clients, system lists) is still loaded here.
 */
const AllIssues = ({
  filter,
  showEpics = false,
  onShowEpicsChange,
  onFilterChange,
  onFilterReset,
  syncToken = 0,
  onOpenWorkItemDetail,
  registerGlobalListRefetch,
  saveFilterProps = {},
}) => {
  const dispatch = useDispatch();

  const issuesData = useSelector(selectIssuesData);
  const loading = useSelector(selectIssuesLoading);
  const error = useSelector(selectIssuesError);
  const usersData = useSelector(selectUsersData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const clientsData = useSelector(selectClientsData);

  const [currentPage, setCurrentPage] = useState(1);
  const [typeOptions, setTypeOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
    dispatch(fetchProjectsData({ getAll: true }));
    dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "WORK_ITEM_TYPE", context: "issue-list-type" }))
      .unwrap()
      .then((list) => {
        const options = Array.isArray(list)
          ? list.map((item) => mapSystemListItemToOption(item))
          : [];
        setTypeOptions(options);
      })
      .catch(() => setTypeOptions([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "PRIORITY", context: "issue-list-priority" }))
      .unwrap()
      .then((list) => {
        const options = Array.isArray(list)
          ? list.map((item) => mapSystemListItemToOption(item))
          : [];
        setPriorityOptions(options);
      })
      .catch(() => setPriorityOptions([]));
  }, [dispatch]);

  const assigneeOptions = useMemo(
    () => getListFromApiResponse(usersData).map(mapUserToAssigneeOptionForDropdown),
    [usersData]
  );

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {error}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <IssueList
      issuesData={issuesData ?? { list: [] }}
      loading={loading}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      listingFilter={filter}
      showEpics={showEpics}
      onShowEpicsChange={onShowEpicsChange}
      handleFilterChange={onFilterChange}
      handleFilterReset={onFilterReset}
      syncAppliedFromUrlToken={syncToken}
      assigneeOptions={assigneeOptions}
      filterMode="all"
      projectsData={projectsData}
      clientsData={clientsData}
      typeOptions={typeOptions}
      statusOptions={ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS}
      priorityOptions={priorityOptions}
      onOpenWorkItemDetail={onOpenWorkItemDetail}
      registerGlobalListRefetch={registerGlobalListRefetch}
      {...saveFilterProps}
    />
  );
};

export default AllIssues;
